import React, { useState } from 'react'
import clsx from 'clsx'
import {
  makeStyles,
  Theme,
  createStyles,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Tab,
  Tabs,
  Typography,
  Box,
} from '@material-ui/core'
import { KeyboardArrowDown } from '@material-ui/icons'
import CMSText from '../Global/CMSText'
import { PrismicShippingCost } from '@/interfaces/common/prismicProduct'
import { CommonInformation } from '../../interfaces/common/prismicGeneralConfig'
import ShippingCostsTable from './ShippingCostsTable'
import { isB2B } from '../../utils/constants'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    accordions: {
      paddingTop: theme.spacing(8),
      '& .MuiAccordion': {
        '&-root': {
          borderRadius: 0,
          boxShadow: 'none !important',
          '&:before': {
            backgroundColor: theme.palette.common.black,
          },
          '&.Mui-expanded': {
            margin: 0,
          },
        },
      },
      '& .MuiAccordionSummary': {
        '&-root': {
          paddingLeft: 0,
          paddingRight: 0,
          color: theme.palette.common.black,
          '&.Mui-expanded': {
            minHeight: '1em',
          },
        },
        '&-expandIcon': {
          color: theme.palette.common.black,
        },
        '&-content': {
          margin: theme.spacing(1, 0),
          '&.Mui-expanded': {
            margin: theme.spacing(1, 0),
            '& p': {
              fontWeight: 700,
            },
          },
          '& p': {
            margin: 0,
          },
        },
      },
      '& .MuiAccordionDetails': {
        '&-root': {
          padding: theme.spacing(1, 0),
          '& .MuiTypography-root': {
            fontSize: '95%',
            '& *': {
              marginTop: 0,
              marginBottom: theme.spacing(1),
              '&:last-child': {
                marginBottom: 0,
              },
            },
            '& img': {
              display: 'block',
              maxWidth: '100%',
              height: 'auto',
              marginLeft: 'auto',
              marginRight: 'auto',
            },
          },
        },
      },
    },
    tabs: {
      paddingTop: theme.spacing(8),
      '& .MuiTabs': {
        '&-root': {
          minHeight: 0,
        },
        '&-indicator': {
          backgroundColor: theme.palette.common.black,
        },
      },
      '& .MuiTab': {
        '&-root': {
          minWidth: 0,
          minHeight: 0,
          padding: theme.spacing(1, 2),
          fontSize: theme.typography.pxToRem(14),
        },
        '&-wrapper': {
          '& p': {
            margin: 0,
            textTransform: 'none',
          },
        },
      },
      '& .MuiBox': {
        '&-root': {
          fontSize: '95%',
          '& * ': {
            marginTop: 0,
            marginBottom: theme.spacing(1),
            '&:last-child': {
              marginBottom: 0,
            },
          },
          '& img': {
            display: 'block',
            maxWidth: '100%',
            height: 'auto',
            marginLeft: 'auto',
            marginRight: 'auto',
          },
        },
      },
    },
  })
)

interface TabPanelProps {
  children?: React.ReactNode
  index: any
  value: any
  className?: string
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`product-tabpanel-${index}`}
      aria-labelledby={`product-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={1}>{children}</Box>}
    </div>
  )
}

type TProps = {
  datas: CommonInformation[]
  layout: boolean
  shippingCosts?: PrismicShippingCost[]
}

const CommonInformations = ({ datas, layout, shippingCosts }: TProps) => {
  const classes = useStyles()
  const [currentInfo, setCurrentInfo] = useState(0)
  const [expanded, setExpanded] = React.useState<string | false>(false)

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setCurrentInfo(newValue)
  }

  const handleAccordionChange =
    (panel: string) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false)
    }

  return datas && datas.length > 0 ? (
    <div>
      {layout ? (
        <div className={clsx('product-accordions', classes.accordions)}>
          {datas.map((el, index) => (
            <Accordion
              expanded={expanded === `panel-${index}`}
              key={index}
              onChange={handleAccordionChange(`panel-${index}`)}
              className={'product-accordion'}
            >
              <AccordionSummary
                className={'product-accordion__summary'}
                expandIcon={<KeyboardArrowDown />}
              >
                <Typography component="div">
                  <CMSText data={el.label} />
                </Typography>
              </AccordionSummary>
              <AccordionDetails className={'product-accordion__details'}>
                <Typography variant={'body1'} component={'div'}>
                  <CMSText data={el.content} />
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </div>
      ) : (
        <div className={classes.tabs}>
          <Tabs
            onChange={handleTabChange}
            value={currentInfo}
            variant={'scrollable'}
            scrollButtons={'auto'}
            className={'product-tabs'}
          >
            {datas.map((el, index) => (
              <Tab
                value={index}
                key={index}
                label={<CMSText data={el.label} />}
                id={`product-tab-${index}`}
                aria-controls={`product-tabpanel-${index}`}
                className={'product-tab'}
              />
            ))}
          </Tabs>
          {datas.map((el, index) => (
            <TabPanel
              value={currentInfo}
              key={index}
              index={index}
              className={'product-tabpanel'}
            >
              <CMSText data={el.content} />
              {isB2B &&
                el.display_shipping_costs &&
                shippingCosts &&
                shippingCosts.length > 0 && (
                  <ShippingCostsTable shippingCosts={shippingCosts} />
                )}
            </TabPanel>
          ))}
        </div>
      )}
    </div>
  ) : null
}

export default CommonInformations
